import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import restaurantService from '../../services/restaurantService';
import { useRealtime } from '../../contexts/RealtimeContext';
import { useToast } from '../../contexts/ToastContext';
import {
  ORDER_COMPLETED_EVENT,
  ORDER_PLACED_EVENT,
  readOngoingOrderContext,
} from '../../utils/ongoingOrderContext';
import { extractAppNameFromPath, extractOrderIdFromPath, shouldShowOngoingOrderBar } from '../../utils/restaurantRoutes';
import { isCompletedOrderStatus } from '../../utils/orderStatus';
import useOrderPolling from '../../hooks/useOrderPolling';
import OrderFeedbackModal from './OrderFeedbackModal';

interface WatchedOrder {
  appName: string;
  orderId: number;
}

const dismissedKey = (orderId: number) => `feedback-modal-dismissed-${orderId}`;

function isDismissed(orderId: number): boolean {
  try {
    return Boolean(sessionStorage.getItem(dismissedKey(orderId)));
  } catch {
    return false;
  }
}

function markDismissed(orderId: number): void {
  try {
    sessionStorage.setItem(dismissedKey(orderId), '1');
  } catch {
    // ignore storage errors
  }
}

/**
 * App-wide feedback prompt: opens the feedback form as soon as the guest's order is
 * marked completed, whichever page they are on. The order details page for that same
 * order shows its own modal, so it is skipped there.
 */
const OrderFeedbackPrompt: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.user);
  const { subscribeOrderStatus } = useRealtime();
  const { showToast } = useToast();

  const [active, setActive] = useState<WatchedOrder | null>(null);
  const watchedRef = useRef<WatchedOrder | null>(null);
  const handledRef = useRef<Set<number>>(new Set());
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const syncWatched = useCallback(() => {
    const ctx = readOngoingOrderContext();
    if (ctx?.appName && ctx.orderId) {
      watchedRef.current = { appName: ctx.appName, orderId: Number(ctx.orderId) };
    }
  }, []);

  useEffect(() => {
    syncWatched();
  }, [location.pathname, syncWatched]);

  useEffect(() => {
    window.addEventListener(ORDER_PLACED_EVENT, syncWatched);
    return () => window.removeEventListener(ORDER_PLACED_EVENT, syncWatched);
  }, [syncWatched]);

  const promptIfEligible = useCallback(async (appName: string, orderId: number) => {
    if (!orderId || handledRef.current.has(orderId) || isDismissed(orderId)) return;
    if (extractOrderIdFromPath(pathnameRef.current) === orderId) return;
    handledRef.current.add(orderId);
    if (watchedRef.current?.orderId === orderId) {
      watchedRef.current = null;
    }
    try {
      const res = await restaurantService.getOrderFeedback(appName, orderId);
      if (res.success && res.eligible && !res.submitted) {
        setActive({ appName, orderId });
      }
    } catch {
      // feedback is optional — never block the current page; allow a later retry
      handledRef.current.delete(orderId);
    }
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<WatchedOrder>).detail;
      if (detail?.appName && detail.orderId) {
        void promptIfEligible(detail.appName, Number(detail.orderId));
      }
    };
    window.addEventListener(ORDER_COMPLETED_EVENT, handler);
    return () => window.removeEventListener(ORDER_COMPLETED_EVENT, handler);
  }, [promptIfEligible]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return subscribeOrderStatus((payload) => {
      const status = payload.orderStatus || payload.status;
      if (!status || !isCompletedOrderStatus(status)) return;
      const orderId = Number(payload.orderId);
      const watched = watchedRef.current;
      const appName =
        (watched?.orderId === orderId ? watched.appName : null) ||
        extractAppNameFromPath(pathnameRef.current) ||
        watched?.appName;
      if (appName) {
        void promptIfEligible(appName, orderId);
      }
    });
  }, [isAuthenticated, subscribeOrderStatus, promptIfEligible]);

  // Fallback when CometD is down: the ongoing-order bar polls on menu/cart pages,
  // so only poll here on the other pages.
  const pollWatched = useCallback(async () => {
    const watched = watchedRef.current;
    if (!watched) return;
    const res = await restaurantService.getOngoingOrder(watched.appName);
    const stillOngoing =
      res.success &&
      res.hasOngoingOrder &&
      Number(res.order?.orderId) === watched.orderId &&
      !isCompletedOrderStatus(res.order?.userOrder?.orderStatus || '');
    if (!stillOngoing) {
      await promptIfEligible(watched.appName, watched.orderId);
    }
  }, [promptIfEligible]);

  useOrderPolling(
    pollWatched,
    isAuthenticated && !active && !shouldShowOngoingOrderBar(location.pathname)
  );

  const close = useCallback(() => {
    setActive((current) => {
      if (current) markDismissed(current.orderId);
      return null;
    });
  }, []);

  const handleNotify = useCallback(
    (message: string, type: 'success' | 'error' | 'info') => showToast(message, type),
    [showToast]
  );

  if (!isAuthenticated || !active) return null;

  return (
    <OrderFeedbackModal
      appName={active.appName}
      orderId={active.orderId}
      open
      onClose={close}
      onSubmitted={close}
      onNotify={handleNotify}
    />
  );
};

export default OrderFeedbackPrompt;
